import React, { useEffect, useState } from 'react'

import { Icon, Icons, Popover } from '@clientbase/clientbase-library'
import { ExpandLess, ExpandMore } from '@mui/icons-material'
import {
  Collapse,
  List,
  Box,
  ListItemButton as ListItemButtonMui,
  ListItemText,
  Tooltip,
  Typography
} from '@mui/material'
import { useAuthContext } from 'context/AuthContext'
import { GROUP_UNAVAILABLE_HINT } from 'lib/merchant-group'
import mixpanel from 'mixpanel-browser'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { ParsedUrlQueryInput } from 'querystring'

import { SubheaderProps } from 'components/Drawer/Drawer'
import Upgrade from 'components/Upgrade/Upgrade'

import { theme } from 'styles/theme'

import { MenuParams } from './Menu.interface'
import * as S from './Menu.styles'

// SC-10999: telas por empresa continuam no menu da visão multi-empresas,
// esmaecidas e com o aviso GROUP_UNAVAILABLE_HINT.

const groupUnavailableSx = { opacity: 0.45 }

const GroupUnavailableIcon = () => (
  <Icon
    icon="lock"
    width="14px"
    height="14px"
    colorSX={theme.palette.neutral[400]}
  />
)

// Esmaece o item indisponível no grupo e, com `hint`, explica no tooltip.
// Item com subitens fica sem tooltip: ele abre o popover dos subitens ao passar
// o mouse, e cada subitem traz o próprio aviso.
const GroupUnavailableTooltip = ({
  unavailable,
  hint = true,
  children
}: {
  unavailable?: boolean
  hint?: boolean
  children: React.ReactElement
}) => {
  if (!unavailable) return children

  const muted = <Box sx={groupUnavailableSx}>{children}</Box>
  if (!hint) return muted

  return (
    <Tooltip title={GROUP_UNAVAILABLE_HINT} placement="right" arrow>
      {muted}
    </Tooltip>
  )
}

export const Menu: React.FC<MenuParams> = ({
  icon = 'add',
  label,
  router,
  handleClick,
  isExpanded,
  subheader,
  openMenu,
  handleOpenMenu,
  option,
  expandDrawer,
  news,
  groupUnavailable
}) => {
  const { push, pathname, query: queryRouter } = useRouter()
  const { capabilities, setModalPricing } = useAuthContext()
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  let closeTimeout: NodeJS.Timeout

  const handleOpen = (e: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget)
    clearTimeout(closeTimeout)
  }

  const handleClose = () => {
    closeTimeout = setTimeout(() => {
      setAnchorEl(null)
    }, 50)
  }

  const open = Boolean(anchorEl)

  const onMenuClick = (
    event?: React.MouseEvent<HTMLDivElement>,
    query?: ParsedUrlQueryInput,
    routerOverride?: string
  ) => {
    const mouseEvent = event as unknown as React.MouseEvent
    const isNewTab = mouseEvent.ctrlKey || mouseEvent.metaKey

    const effectiveRouter = routerOverride || router
    const queryString = new URLSearchParams(
      query as Record<string, string>
    ).toString()
    const newUrl = `${effectiveRouter}${queryString ? `?${queryString}` : ''}`

    if (isNewTab) {
      window.open(newUrl, '_blank')
    } else {
      push({ query, pathname: effectiveRouter })
    }

    handleClick()
  }

  const onMenuClickRouter = (
    event?: React.MouseEvent<HTMLDivElement>,
    subRouter?: string,
    routerOverride?: string
  ) => {
    const mouseEvent = event as unknown as React.MouseEvent
    const isNewTab = mouseEvent.ctrlKey || mouseEvent.metaKey

    const effectiveRouter = routerOverride || router
    const newUrl = `${effectiveRouter}${subRouter || ''}`

    if (isNewTab) {
      window.open(newUrl, '_blank')
    } else {
      push({ pathname: `${effectiveRouter}${subRouter || ''}` })
    }

    handleClick()
  }

  const checkUrlMatch = (router: string, subRouter?: string) => {
    if (subRouter) {
      return pathname.split('/').includes(subRouter.split('/')[1])
    }
    if (pathname.split('/').length > 2) {
      return pathname.split('/').includes(router.substring(1))
    }
    return pathname === router
  }

  const activeSubMenu = (
    router: string,
    tab?: number | string,
    subRouter?: string,
    routerOverride?: string
  ) => {
    if (routerOverride) {
      return checkUrlMatch(routerOverride)
    }
    if (tab || tab === 0) {
      return (
        (tab === Number(queryRouter.tab) || tab === queryRouter.tab) &&
        checkUrlMatch(router)
      )
    } else {
      return (
        pathname.split('/').includes(subRouter?.split('/')[1] as string) &&
        checkUrlMatch(router, subRouter)
      )
    }
  }

  const handleOpenMenuAndClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (subheader && option) {
      handleOpenMenu(option)
      !!expandDrawer && expandDrawer()
    } else {
      onMenuClick(event)
    }
  }

  const handleClickSubHeader = (
    event: React.MouseEvent<HTMLDivElement>,
    item: SubheaderProps
  ) => {
    if (item?.upgrade && !capabilities?.workflow?.has_access) {
      event.preventDefault()
      event.stopPropagation()
      setModalPricing({
        isOpen: true,
        plan: capabilities?.workflow?.plan || 'grow',
        resourceName: 'Workflows'
      })
      return
    }

    if (item?.query) {
      onMenuClick(event, item.query, item.router)
    } else {
      onMenuClickRouter(event, item.subRouter, item.router)
    }
  }

  const makeLinkHref = (item: SubheaderProps) => {
    const effectiveRouter = item?.router || router
    if (item?.query) {
      const queryString = new URLSearchParams(
        item.query as Record<string, string>
      ).toString()

      return `${effectiveRouter}?${queryString || ''}`
    } else if (item?.router) {
      return `${effectiveRouter}${item?.subRouter || ''}`
    } else {
      return `${effectiveRouter}?${item?.subRouter || ''}`
    }
  }

  useEffect(() => {
    if (checkUrlMatch(router) && subheader) {
      handleOpenMenu(router === '/cobrancas' ? 'receives' : 'clients')
    }
  }, [])

  useEffect(() => {
    if (isExpanded || openMenu[option as string]) {
      handleClose()
    }
  }, [isExpanded, openMenu])
  const mixpanelTyped = mixpanel as unknown as { __loaded: string }
  const withHighlight = subheader?.filter((item) => item?.highlight)

  const ListItemButton = (
    <S.ListItemButton
      isExpanded={isExpanded}
      active={checkUrlMatch(router)}
      onClick={handleOpenMenuAndClick}
      onMouseEnter={(e) => {
        if (
          (!openMenu[option as string] && isExpanded && subheader) ||
          (!isExpanded && subheader)
        ) {
          handleOpen(e)
        }
      }}
      onMouseLeave={() => {
        handleClose()
      }}
    >
      <S.ListItemIcon>
        {typeof icon !== 'string' ? (
          React.cloneElement(icon as any, {
            style: { width: '20px', height: '20px' }
          })
        ) : (
          <Icon icon={icon as Icons} width="20px" height="20px" />
        )}
      </S.ListItemIcon>
      {isExpanded && (
        <>
          <Box display="flex" alignItems="center" gap={1} flex={1}>
            <S.ListItemText primary={label} active={checkUrlMatch(router)} />
            {groupUnavailable && <GroupUnavailableIcon />}
            {news && (
              <Typography
                variant="xsBold"
                sx={{
                  backgroundColor: theme.palette.primary.main,
                  padding: '2px 6px',
                  borderRadius: '6px',
                  fontSize: '0.625rem',
                  color: '#fff',
                  whiteSpace: 'nowrap'
                }}
              >
                NOVO
              </Typography>
            )}
          </Box>
          {subheader ? (
            <Box
              sx={{ marginLeft: 'auto', display: 'flex', alignItems: 'center' }}
            >
              {openMenu[option as string] ? <ExpandLess /> : <ExpandMore />}
            </Box>
          ) : null}
        </>
      )}
    </S.ListItemButton>
  )
  return (
    <Box>
      <GroupUnavailableTooltip unavailable={groupUnavailable} hint={!subheader}>
        {!subheader ? (
          <Link
            href={router}
            onClick={(e) => e.preventDefault()}
            style={{ textDecoration: 'none', color: 'inherit' }}
          >
            {ListItemButton}
          </Link>
        ) : (
          <>{ListItemButton}</>
        )}
      </GroupUnavailableTooltip>
      {subheader && isExpanded && (
        <Collapse
          in={
            withHighlight && withHighlight.length > 0
              ? true
              : option
              ? openMenu[option]
              : false
          }
          timeout="auto"
          unmountOnExit
        >
          <List component="div" disablePadding>
            {subheader.map((item, index) => (
              <GroupUnavailableTooltip
                key={index}
                unavailable={item?.groupUnavailable}
              >
                <Link
                  href={makeLinkHref(item)}
                  onClick={(e) => e.preventDefault()}
                  style={{ textDecoration: 'none', color: 'inherit' }}
                >
                  <S.ListItemSubHeader
                    highlight={item?.highlight}
                    activeSubmenu={activeSubMenu(
                      router,
                      item?.tab,
                      item?.subRouter,
                      item?.router
                    )}
                    onClick={(e) => {
                      mixpanelTyped.__loaded &&
                        mixpanel.track('Clique no menu', {
                          origem: 'sidebar',
                          menu: label,
                          submenu: item.label
                        }),
                        handleClickSubHeader(e, item)
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <S.ListItemText
                        primary={item?.label}
                        active={activeSubMenu(
                          router,
                          item?.tab,
                          item?.subRouter,
                          item?.router
                        )}
                        highlight={item?.highlight}
                      />
                      {item?.icon && (
                        <Icon
                          icon={item.icon}
                          width="14px"
                          height="14px"
                          colorSX={theme.palette.primary.main}
                        />
                      )}
                      {(item?.tag || item?.news) && (
                        <Typography
                          variant="xsBold"
                          sx={{
                            backgroundColor: theme.palette.primary.main,
                            padding: '2px 6px',
                            borderRadius: '6px',
                            fontSize: '0.625rem',
                            color: '#fff',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {item?.tag || 'BETA'}
                        </Typography>
                      )}
                      {item?.upgrade && !capabilities?.workflow?.has_access && (
                        <Upgrade
                          plan={capabilities?.workflow?.plan}
                          resourceName="Workflows"
                        />
                      )}
                      {item?.coexAlert &&
                        capabilities?.whatsapp_coexistence?.status ===
                          'failed' && (
                          <Tooltip
                            title="WhatsApp Business desativado — ação necessária"
                            arrow
                            placement="right"
                          >
                            <Box display="flex" alignItems="center">
                              <Icon
                                icon="error"
                                width="14px"
                                height="14px"
                                colorSX={theme.palette.error.main}
                              />
                            </Box>
                          </Tooltip>
                        )}
                      {item?.groupUnavailable && <GroupUnavailableIcon />}
                    </Box>
                  </S.ListItemSubHeader>
                </Link>
              </GroupUnavailableTooltip>
            ))}
          </List>
        </Collapse>
      )}

      <Popover
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        sx={{ pointerEvents: 'none' }}
        PaperProps={{
          onMouseEnter: () => clearTimeout(closeTimeout),
          onMouseLeave: handleClose,
          sx: {
            pointerEvents: 'auto',
            ml: 1,
            p: '12px 8px',
            width: '200px',
            borderRadius: '12px',
            bgcolor: theme.palette.neutral[800]
          }
        }}
      >
        <List component="div" disablePadding>
          {subheader &&
            subheader.map((item, index) => (
              <GroupUnavailableTooltip
                key={index}
                unavailable={item?.groupUnavailable}
              >
                <Link
                  href={makeLinkHref(item)}
                  onClick={(e) => {
                    e.preventDefault(),
                      mixpanelTyped.__loaded &&
                        mixpanel.track('Clique no menu', {
                          origem: 'popover',
                          menu: label,
                          submenu: item.label
                        })
                  }}
                  style={{ textDecoration: 'none', color: 'inherit' }}
                >
                  <ListItemButtonMui
                    onClick={(e) => {
                      handleClickSubHeader(e, item)
                    }}
                    sx={{
                      p: '4px 16px',
                      width: '100%',
                      background: item?.highlight
                        ? theme.palette.success[300]
                        : activeSubMenu(
                            router,
                            item?.tab,
                            item?.subRouter,
                            item?.router
                          )
                        ? '#fff'
                        : 'none',
                      borderRadius: '6px',

                      color: item?.highlight ? '#fff' : '#000',
                      ':hover': {
                        background: item?.highlight
                          ? theme.palette.success[300]
                          : ''
                      }
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <ListItemText
                        primary={item?.label}
                        sx={{
                          span: {
                            color: item?.highlight
                              ? '#fff'
                              : activeSubMenu(
                                  router,
                                  item?.tab,
                                  item?.subRouter,
                                  item?.router
                                )
                              ? theme.palette.neutral[200]
                              : theme.palette.neutral[400],
                            fontSize: '14px',
                            fontWeight: activeSubMenu(
                              router,
                              item?.tab,
                              item?.subRouter,
                              item?.router
                            )
                              ? 600
                              : 400
                          }
                        }}
                      />
                      {item?.icon && (
                        <Icon
                          icon={item.icon}
                          width="14px"
                          height="14px"
                          colorSX={theme.palette.primary.main}
                        />
                      )}
                      {(item?.tag || item?.news) && (
                        <Typography
                          variant="xsBold"
                          sx={{
                            backgroundColor: theme.palette.primary.main,
                            padding: '2px 6px',
                            borderRadius: '6px',
                            fontSize: '0.625rem',
                            color: '#fff',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {item?.tag || 'BETA'}
                        </Typography>
                      )}
                      {item?.upgrade && !capabilities?.workflow?.has_access && (
                        <Upgrade
                          plan={capabilities?.workflow?.plan}
                          resourceName="Workflows"
                        />
                      )}
                      {item?.coexAlert &&
                        capabilities?.whatsapp_coexistence?.status ===
                          'failed' && (
                          <Tooltip
                            title="WhatsApp Business desativado — ação necessária"
                            arrow
                            placement="right"
                          >
                            <Box display="flex" alignItems="center">
                              <Icon
                                icon="error"
                                width="14px"
                                height="14px"
                                colorSX={theme.palette.error.main}
                              />
                            </Box>
                          </Tooltip>
                        )}
                      {item?.groupUnavailable && <GroupUnavailableIcon />}
                    </Box>
                  </ListItemButtonMui>
                </Link>
              </GroupUnavailableTooltip>
            ))}

          {!subheader && (
            <GroupUnavailableTooltip unavailable={groupUnavailable}>
              <Link
                href={router}
                key={0}
                onClick={(e) => e.preventDefault()}
                style={{ textDecoration: 'none', color: 'inherit' }}
              >
                <ListItemButtonMui
                  onClick={() => push(router)}
                  sx={{
                    p: '4px 16px',
                    width: '100%',
                    background: pathname === router ? '#fff' : 'none',
                    borderRadius: '6px',

                    color: '#000'
                  }}
                >
                  <ListItemText
                    primary={label}
                    sx={{
                      span: {
                        color:
                          pathname === router
                            ? theme.palette.neutral[200]
                            : theme.palette.neutral[400],
                        fontSize: '14px',
                        fontWeight: pathname === router ? 600 : 400
                      }
                    }}
                  />
                </ListItemButtonMui>
              </Link>
            </GroupUnavailableTooltip>
          )}
        </List>
      </Popover>
    </Box>
  )
}
